import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { MODAL_SIZE } from "@constants/appDefaults";
import { useFocusTrap } from "@hooks/useFocusTrap";
import styles from "./Modal.module.css";

const SIZE_CLASSES = {
  [MODAL_SIZE.SMALL]: styles.modernModalSmall,
  [MODAL_SIZE.MEDIUM]: styles.modernModalMedium,
  [MODAL_SIZE.LARGE]: styles.modernModalLarge,
  [MODAL_SIZE.XLARGE]: styles.modernModalXlarge,
  [MODAL_SIZE.FULL]: styles.modernModalFull,
};

/**
 * Modern Modal Component
 * 
 * A clean, modern modal component with smooth animations and accessibility features.
 * Designed to replace the old Popup component with better UX and visual design.
 * 
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {Function} props.onClose - Close handler
 * @param {ReactNode} props.children - Modal content
 * @param {Object} props.customStyles - Custom styles for modal wrapper
 * @param {boolean} props.disableBackdropClose - Prevent closing on backdrop click
 * @param {string} props.size - Modal size: 'small', 'medium', 'large', 'xlarge', 'full'
 * @param {boolean} props.showCloseButton - Show close button in header
 * @param {ReactNode} props.headerContent - Additional content to render in header (below title)
 * @param {ReactNode} props.titleBadge - Badge/supplement to render inline with the title (e.g. Beta)
 * @param {ReactNode} props.footer - Optional footer (e.g. actions bar); rendered in a fixed bar at bottom, outside scroll
 */
export const Modal = ({
  title,
  onClose,
  children,
  customStyles = {},
  disableBackdropClose = false,
  disableEscapeClose = disableBackdropClose,
  size = MODAL_SIZE.MEDIUM,
  showCloseButton = true,
  closeLabel = "Close modal",
  className = "",
  description,
  headerContent,
  titleBadge,
  footer,
  zIndex,
  isTop = true,
  ariaHidden,
  inert: inertProp,
}) => {
  const hidden = ariaHidden ?? !isTop;
  const inert = inertProp ?? (!isTop ? "" : undefined);
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    // Store the previously focused element
    previousActiveElement.current = document.activeElement;

    return () => {
      // Restore focus to previous element
      if (previousActiveElement.current?.isConnected) {
        previousActiveElement.current.focus();
      }
    };
  }, []);

  useFocusTrap(modalRef, isTop);

  const handleBackdropClick = (e) => {
    // Don't close if clicking on datepicker popper or its children
    const isDatepickerClick = e.target.closest('.react-datepicker__popper') || 
                              e.target.closest('.filter-datepicker-popper') ||
                              e.target.closest('.modal-datepicker-popper') ||
                              e.target.closest('.react-datepicker') ||
                              e.target.classList.contains('react-datepicker-ignore-onclickoutside');
    
    if (
      e.target === e.currentTarget &&
      isTop &&
      !disableBackdropClose &&
      !isDatepickerClick
    ) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={styles.modernModalBackdrop}
      style={zIndex != null ? { zIndex } : undefined}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal={isTop}
      aria-hidden={hidden}
      inert={inert}
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={description ? descriptionId : undefined}
    >
      <div
        className={`${styles.modernModalWrapper} ${SIZE_CLASSES[size] || styles.modernModalMedium} ${className}`}
        style={customStyles}
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape" && isTop) {
            e.preventDefault();
            e.stopPropagation();
            if (!disableEscapeClose) onClose();
          }
        }}
        data-is-top={String(isTop)}
      >
        {(title || showCloseButton || headerContent || titleBadge) && (
          <div className={styles.modernModalHeader}>
            <div className={styles.modernModalHeaderMain}>
              {title && (
                <div className={styles.modernModalHeaderContent}>
                  <div className={styles.modernModalTitleRow}>
                    <h2 id={titleId} className={styles.modernModalTitle}>
                      {title}
                    </h2>
                    {titleBadge && (
                      <span className={styles.modernModalTitleBadge}>
                        {titleBadge}
                      </span>
                    )}
                  </div>
                  {description && (
                    <p id={descriptionId} className={styles.modernModalDescription}>
                      {description}
                    </p>
                  )}
                </div>
              )}
              {showCloseButton && (
                <button
                  className={styles.modernModalClose}
                  onClick={onClose}
                  aria-label={closeLabel}
                  type="button"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
            {headerContent && (
              <div className={styles.modernModalHeaderExtra}>
                {headerContent}
              </div>
            )}
          </div>
        )}

        <div className={styles.modernModalContent}>{children}</div>
        {footer && <div className={styles.modernModalFooter}>{footer}</div>}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
